import { MouseEvent as ReactMouseEvent, ReactNode, RefObject, TouchEvent as ReactTouchEvent } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { TranslationKey } from "../../../shared/i18n/i18n";
import { AdminRoutePlaceholder } from "../common/AdminRoutePlaceholder";
import { AdminAdministrationPage } from "../profile/AdminAdministrationPage";
import { AdminProfilePage } from "../profile/AdminProfilePage";
import { AdminOrganizationContextCacheProvider } from "./AdminOrganizationContextCache";
import { AdminLoadingProvider } from "./AdminLoadingContext";
import { AdminShellHeader } from "./AdminShellHeader";
import { AdminShellLoadingBar } from "./AdminShellLoadingBar";
import { AdminWorkspace } from "./AdminWorkspace";

type AdminShellProps = {
  banner?: ReactNode;
  currentUserName: string;
  currentUserSuperAdmin: boolean;
  isDesktopNavHidden: boolean;
  isLeftPaneCollapsed: boolean;
  isMobileNavOpen: boolean;
  isMobileViewport: boolean;
  navigatorReloadKey: number;
  isProfileMenuOpen: boolean;
  leftPaneWidth: number;
  onCloseDesktopNav: () => void;
  onCloseMobileNav: () => void;
  onOpenDesktopNav: () => void;
  onSignOut: () => void;
  onStartLeftPaneResize: (event: ReactMouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement>) => void;
  onToggleLeftPane: () => void;
  onToggleMobileNav: () => void;
  onToggleProfileMenu: () => void;
  profileMenuRef: RefObject<HTMLDivElement | null>;
  t: (key: TranslationKey) => string;
};

const workspaceRoutes = [
  "/a",
  "/a/organizations",
  "/a/organizations/create",
  "/a/organization/:organizationId",
  "/a/organization/:organizationId/details",
  "/a/organization/:organizationId/domains",
  "/a/organization/:organizationId/internals",
  "/a/organization/:organizationId/users",
  "/a/organization/:organizationId/subscription",
  "/a/organization/:organizationId/site/:siteId",
  "/a/organization/:organizationId/site/:siteId/details",
  "/a/organization/:organizationId/site/:siteId/sign/:signId",
  "/a/organization/:organizationId/site/:siteId/sign/:signId/details",
  "/a/organization/:organizationId/site/:siteId/sign/:signId/urls",
  "/a/organization/:organizationId/sites",
  "/a/organization/:organizationId/sites/create",
  "/a/organization/:organizationId/site/:siteId/signs",
  "/a/organization/:organizationId/site/:siteId/signs/create",
  "/a/organization/:organizationId/site/:siteId/sign/:signId/translations",
  "/a/organization/:organizationId/site/:siteId/sign/:signId/translations/create",
  "/a/organization/:organizationId/site/:siteId/sign/:signId/translation/:translationId",
  "/a/organization/:organizationId/site/:siteId/sign/:signId/translation/:translationId/details",
];

export const AdminShell = ({
  banner = null,
  currentUserName,
  currentUserSuperAdmin,
  isDesktopNavHidden,
  isLeftPaneCollapsed,
  isMobileNavOpen,
  isMobileViewport,
  navigatorReloadKey,
  isProfileMenuOpen,
  leftPaneWidth,
  onCloseDesktopNav,
  onCloseMobileNav,
  onOpenDesktopNav,
  onSignOut,
  onStartLeftPaneResize,
  onToggleLeftPane,
  onToggleMobileNav,
  onToggleProfileMenu,
  profileMenuRef,
  t,
}: AdminShellProps) => {
  return (
    <AdminLoadingProvider>
      <AdminOrganizationContextCacheProvider>
        <main className="admin-shell">
          <AdminShellHeader
            currentUserName={currentUserName}
            currentUserSuperAdmin={currentUserSuperAdmin}
            isMobileNavOpen={isMobileNavOpen}
            isMobileViewport={isMobileViewport}
            isProfileMenuOpen={isProfileMenuOpen}
            onSignOut={onSignOut}
            onToggleMobileNav={onToggleMobileNav}
            onToggleProfileMenu={onToggleProfileMenu}
            profileMenuRef={profileMenuRef}
            t={t}
          />
          <AdminShellLoadingBar />
          {banner}
          <section className="admin-shell-content">
            <Routes>
              {workspaceRoutes.map((path) => (
                <Route
                  element={(
                    <AdminWorkspace
                      currentUserSuperAdmin={currentUserSuperAdmin}
                      isDesktopNavHidden={isDesktopNavHidden}
                      isLeftPaneCollapsed={isLeftPaneCollapsed}
                      isMobileNavOpen={isMobileNavOpen}
                      isMobileViewport={isMobileViewport}
                      leftPaneWidth={leftPaneWidth}
                      navigatorReloadKey={navigatorReloadKey}
                      onCloseDesktopNav={onCloseDesktopNav}
                      onCloseMobileNav={onCloseMobileNav}
                      onOpenDesktopNav={onOpenDesktopNav}
                      onStartLeftPaneResize={onStartLeftPaneResize}
                      onToggleLeftPane={onToggleLeftPane}
                      t={t}
                    />
                  )}
                  key={path}
                  path={path}
                />
              ))}
              <Route
                element={(
                  <AdminProfilePage t={t} />
                )}
                path="/a/profile/basic"
              />
              <Route
                element={(
                  <AdminProfilePage t={t} />
                )}
                path="/a/profile/terms"
              />
              <Route
                element={(
                  <AdminProfilePage t={t} />
                )}
                path="/a/profile/security"
              />
              <Route
                element={<Navigate replace to="/a/profile/basic" />}
                path="/a/profile"
              />
              <Route
                element={(
                  <AdminAdministrationPage
                    isSuperAdmin={currentUserSuperAdmin}
                    t={t}
                  />
                )}
                path="/a/administration/disable-user"
              />
              <Route
                element={(
                  <AdminAdministrationPage
                    isSuperAdmin={currentUserSuperAdmin}
                    t={t}
                  />
                )}
                path="/a/administration/system-information"
              />
              <Route
                element={<Navigate replace to="/a/administration/system-information" />}
                path="/a/administration"
              />
              <Route
                element={(
                  <AdminRoutePlaceholder
                    descriptionKey="admin.route.preferences.description"
                    titleKey="admin.route.preferences.title"
                  />
                )}
                path="/a/preferences"
              />
              <Route
                element={(
                  <AdminRoutePlaceholder
                    descriptionKey="admin.route.notFound.description"
                    titleKey="admin.route.notFound.title"
                  />
                )}
                path="*"
              />
            </Routes>
          </section>
        </main>
      </AdminOrganizationContextCacheProvider>
    </AdminLoadingProvider>
  );
};
